/** @format */

import React, { useState, useEffect, useMemo } from "react";
import {
  UsersIcon,
  UserGroupIcon,
  AcademicCapIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  CalendarIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  XCircleIcon,
  UserIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { collection, getDocs, query, orderBy, writeBatch, doc, setDoc, deleteDoc, where } from "firebase/firestore";
import { db } from "../firebase/config";
import { parseDate } from "../utils/dateUtils";
import toast from "react-hot-toast";
import LoadingSpinner from "../components/LoadingSpinner";
import { useAuth } from "../contexts/AuthContext";

const EnrolledStudentsPage = () => {
  const { currentUser, userRole, isAdmin } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("");
  const [statusFilter, setStatusFilter] = useState("active");
  const [sortBy, setSortBy] = useState("enrolledAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showEnrollModal, setShowEnrollModal] = useState(false);
  const [showUnenrollModal, setShowUnenrollModal] = useState(false);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [expireDate, setExpireDate] = useState("");

  // Fetch all enrollment data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch enrollments from courseEnrollments collection
        const enrollmentsQuery = query(
          collection(db, "courseEnrollments"),
          orderBy("enrolledAt", "desc")
        );
        const enrollmentsSnapshot = await getDocs(enrollmentsQuery);
        const enrollmentsData = enrollmentsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Fetch courses - filter by author if user is not admin
        let coursesQuery;
        if (isAdmin) {
          coursesQuery = query(collection(db, "courses"));
        } else {
          // For authors, only fetch courses they created
          coursesQuery = query(
            collection(db, "courses"),
            where("author_id", "==", currentUser?.uid)
          );
        }
        const coursesSnapshot = await getDocs(coursesQuery);
        const coursesData = coursesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Fetch users
        const usersQuery = query(collection(db, "users"));
        const usersSnapshot = await getDocs(usersQuery);
        const usersData = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Combine enrollment data with course and user information
        // Only include enrollments for courses the user has access to
        const enrichedEnrollments = enrollmentsData
          .map((enrollment) => {
            const course = coursesData.find((c) => c.id === enrollment.courseId);
            const user = usersData.find((u) => u.id === enrollment.userId);

            return {
              ...enrollment,
              courseData: course,
              userData: user,
              isExpired: enrollment.expireDate
                ? new Date(enrollment.expireDate) < new Date()
                : false,
            };
          })
          .filter((enrollment) => enrollment.courseData); // Only include enrollments with valid course data

        setEnrollments(enrichedEnrollments);
        setCourses(coursesData);
        setAllUsers(usersData);
      } catch (error) {
        console.error("Error fetching enrollment data:", error);
        toast.error("Failed to load enrollment data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser?.uid, isAdmin]);

  // Filter and sort enrollments
  const filteredEnrollments = useMemo(() => {
    let filtered = enrollments.filter((enrollment) => {
      // Search filter
      if (searchQuery) {
        const searchLower = searchQuery.toLowerCase();
        const matchesName = enrollment.userData?.name
          ?.toLowerCase()
          .includes(searchLower);
        const matchesEmail = enrollment.userData?.email
          ?.toLowerCase()
          .includes(searchLower);
        const matchesCourse = enrollment.courseData?.name
          ?.toLowerCase()
          .includes(searchLower);

        if (!matchesName && !matchesEmail && !matchesCourse) {
          return false;
        }
      }

      // Course filter - require course selection
      if (!selectedCourse || enrollment.courseId !== selectedCourse) {
        return false;
      }

      // Status filter
      if (statusFilter === "active" && enrollment.isExpired) {
        return false;
      }
      if (statusFilter === "expired" && !enrollment.isExpired) {
        return false;
      }

      return true;
    });

    // Sort enrollments
    filtered.sort((a, b) => {
      let aValue, bValue;

      switch (sortBy) {
        case "name":
          aValue = a.userData?.name || "";
          bValue = b.userData?.name || "";
          break;
        case "course":
          aValue = a.courseData?.name || "";
          bValue = b.courseData?.name || "";
          break;
        case "enrolledAt":
          aValue = new Date(a.enrolledAt || 0);
          bValue = new Date(b.enrolledAt || 0);
          break;
        case "expireDate":
          aValue = new Date(a.expireDate || 0);
          bValue = new Date(b.expireDate || 0);
          break;
        default:
          aValue = a.enrolledAt || "";
          bValue = b.enrolledAt || "";
      }

      if (sortOrder === "asc") {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [enrollments, searchQuery, selectedCourse, statusFilter, sortBy, sortOrder]);


  const toggleRowExpansion = (enrollmentId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(enrollmentId)) {
      newExpanded.delete(enrollmentId);
    } else {
      newExpanded.add(enrollmentId);
    }
    setExpandedRows(newExpanded);
  };

  // Handle enrolling students
  const handleEnrollStudents = async () => {
    if (selectedStudents.length === 0) {
      toast.error("Please select at least one student");
      return;
    }

    try {
      const batch = writeBatch(db);
      
      for (const studentId of selectedStudents) {
        // Add course to student's Courses subcollection
        const userCoursesRef = collection(db, "users", studentId, "Courses");
        const newCourseRef = doc(userCoursesRef);
        
        batch.set(newCourseRef, {
          course_id: selectedCourse,
          expire_date: expireDate,
          enrolled_at: new Date().toISOString(),
          status: "active",
        });

        // Add to courseEnrollments collection
        const courseEnrollmentsRef = collection(db, "courseEnrollments");
        const newEnrollmentRef = doc(courseEnrollmentsRef);
        
        batch.set(newEnrollmentRef, {
          courseId: selectedCourse,
          userId: studentId,
          expireDate: expireDate,
          enrolledAt: new Date().toISOString(),
          status: "active",
        });
      }

      await batch.commit();
      toast.success(`Successfully enrolled ${selectedStudents.length} students!`);
      
      // Reset state
      setSelectedStudents([]);
      setShowEnrollModal(false);
      
      // Refresh data
      window.location.reload();
    } catch (error) {
      console.error("Error enrolling students:", error);
      toast.error("Failed to enroll students");
    }
  };

  // Handle unenrolling students
  const handleUnenrollStudent = async (enrollmentId, userId) => {
    try {
      // Remove from courseEnrollments collection
      await deleteDoc(doc(db, "courseEnrollments", enrollmentId));
      
      // Remove from user's Courses subcollection
      const userCoursesQuery = query(
        collection(db, "users", userId, "Courses"),
        where("course_id", "==", selectedCourse)
      );
      const userCoursesSnapshot = await getDocs(userCoursesQuery);
      
      const batch = writeBatch(db);
      userCoursesSnapshot.docs.forEach((doc) => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      
      toast.success("Student unenrolled successfully!");
      
      // Refresh data
      window.location.reload();
    } catch (error) {
      console.error("Error unenrolling student:", error);
      toast.error("Failed to unenroll student");
    }
  };

  // Get available students (not enrolled in current course)
  const getAvailableStudents = () => {
    if (!selectedCourse) return [];
    
    const enrolledUserIds = enrollments
      .filter(e => e.courseId === selectedCourse)
      .map(e => e.userId);
    
    return allUsers.filter(user => !enrolledUserIds.includes(user.id));
  };

  const getStatusIcon = (enrollment) => {
    if (enrollment.isExpired) {
      return <XCircleIcon className="h-5 w-5 text-red-500" />;
    } else if (enrollment.status === "active") {
      return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
    } else {
      return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
    }
  };

  const getStatusText = (enrollment) => {
    if (enrollment.isExpired) {
      return "Expired";
    } else if (enrollment.status === "active") {
      return "Active";
    } else {
      return "Inactive";
    }
  };

  const getStatusColor = (enrollment) => {
    if (enrollment.isExpired) {
      return "bg-red-100 text-red-800";
    } else if (enrollment.status === "active") {
      return "bg-green-100 text-green-800";
    } else {
      return "bg-yellow-100 text-yellow-800";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {selectedCourse && (
              <button
                onClick={() => setSelectedCourse("")}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all duration-200 border border-gray-200"
              >
                <ArrowLeftIcon className="h-5 w-5" />
                <span className="font-medium">Back to Courses</span>
              </button>
            )}
            {selectedCourse && courses.find(c => c.id === selectedCourse)?.image_url ? (
              <img
                src={courses.find(c => c.id === selectedCourse).image_url}
                alt={courses.find(c => c.id === selectedCourse)?.name || "Course"}
                className="h-16 w-16 rounded-xl object-cover shadow-lg border-2 border-gray-200"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            <div 
              className={`p-3 rounded-xl ${
                selectedCourse && courses.find(c => c.id === selectedCourse)?.image_url ? 'hidden' : 'bg-blue-100'
              }`}
            >
              <UsersIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Enrolled Students
                {selectedCourse && (
                  <span className="text-lg font-normal text-blue-600 ml-2">
                    - {courses.find(c => c.id === selectedCourse)?.name}
                  </span>
                )}
              </h1>
              <p className="text-gray-600 mt-1">
                {selectedCourse 
                  ? `Students enrolled in ${courses.find(c => c.id === selectedCourse)?.name}`
                  : "Select a course to view enrolled students"}
              </p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold text-blue-600">
              {filteredEnrollments.length}
            </div>
            <div className="text-sm text-gray-500">
              {selectedCourse ? "Course Enrollments" : "Total Enrollments"}
            </div>
            {selectedCourse && (
              <div className="mt-3 flex space-x-2">
                <button
                  onClick={() => {
                    const defaultExpire = new Date();
                    defaultExpire.setMonth(defaultExpire.getMonth() + 3);
                    setExpireDate(defaultExpire.toISOString().slice(0, 16));
                    setShowEnrollModal(true);
                  }}
                  className="flex items-center space-x-1 px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium"
                >
                  <PlusIcon className="h-4 w-4" />
                  <span>Enroll Students</span>
                </button>
              </div>
            )}
          </div>
        </div>
        
        {/* Quick Summary */}
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
          <div className="bg-green-50 rounded-lg p-3 sm:p-4 border border-green-200">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircleIcon className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
              </div>
              <div className="ml-3">
                <div className="text-base sm:text-lg font-semibold text-green-900">
                  {selectedCourse 
                    ? enrollments.filter((e) => e.courseId === selectedCourse && !e.isExpired && e.status === "active").length
                    : enrollments.filter((e) => !e.isExpired && e.status === "active").length}
                </div>
                <div className="text-xs sm:text-sm text-green-700">
                  {selectedCourse ? "Active in Course" : "Total Active"}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-3 sm:p-4 border border-blue-200">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-lg">
                <AcademicCapIcon className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              </div>
              <div className="ml-3">
                <div className="text-base sm:text-lg font-semibold text-blue-900">
                  {selectedCourse 
                    ? enrollments.filter((e) => e.courseId === selectedCourse).length
                    : courses.length}
                </div>
                <div className="text-xs sm:text-sm text-blue-700">
                  {selectedCourse ? "Enrolled in Course" : "Total Courses"}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-3 sm:p-4 border border-purple-200">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-lg">
                <UserGroupIcon className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
              </div>
              <div className="ml-3">
                <div className="text-base sm:text-lg font-semibold text-purple-900">
                  {selectedCourse 
                    ? new Set(enrollments.filter(e => e.courseId === selectedCourse).map(e => e.userId)).size
                    : new Set(enrollments.map(e => e.userId)).size}
                </div>
                <div className="text-xs sm:text-sm text-purple-700">
                  {selectedCourse ? "Unique in Course" : "Total Unique Students"}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters - Only show when course is selected */}
      {selectedCourse && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-2 sm:space-y-0">
            <h3 className="text-lg font-semibold text-gray-900">Filter & Search</h3>
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <AcademicCapIcon className="h-4 w-4" />
              <span className="truncate">{courses.find(c => c.id === selectedCourse)?.name}</span>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {/* Search */}
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search students..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <FunnelIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            {/* Sort */}
            <div className="relative">
              <select
                value={`${sortBy}-${sortOrder}`}
                onChange={(e) => {
                  const [field, order] = e.target.value.split("-");
                  setSortBy(field);
                  setSortOrder(order);
                }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 appearance-none"
              >
                <option value="enrolledAt-desc">Newest First</option>
                <option value="enrolledAt-asc">Oldest First</option>
                <option value="name-asc">Name A-Z</option>
                <option value="name-desc">Name Z-A</option>
                <option value="expireDate-asc">Expires Soon</option>
                <option value="expireDate-desc">Expires Later</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-xl">
              <UsersIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {enrollments.length}
              </div>
              <div className="text-sm text-gray-500">Total Enrollments</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-xl">
              <CheckCircleIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {enrollments.filter((e) => !e.isExpired && e.status === "active")
                  .length}
              </div>
              <div className="text-sm text-gray-500">Active</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-red-100 rounded-xl">
              <XCircleIcon className="h-6 w-6 text-red-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {enrollments.filter((e) => e.isExpired).length}
              </div>
              <div className="text-sm text-gray-500">Expired</div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-purple-100 rounded-xl">
              <AcademicCapIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <div className="text-2xl font-bold text-gray-900">
                {courses.length}
              </div>
              <div className="text-sm text-gray-500">Total Courses</div>
            </div>
          </div>
        </div>
      </div>

      <>
        {/* Course Selection Interface */}
        {!selectedCourse && (
          <div className="space-y-6">
            {/* Course Selection Header */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <div className="text-center">
                <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 mb-4">
                  <AcademicCapIcon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">
                  Select a Course
                </h3>
                <p className="text-gray-600 mb-6">
                  Choose a course below to view all enrolled students and manage enrollments.
                </p>
              </div>
            </div>

            {/* Course Selection Grid */}
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-4">
                Available Courses ({courses.length})
              </h4>
              {courses.length === 0 ? (
                <div className="text-center py-12">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 mb-4">
                    <AcademicCapIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {isAdmin ? "No Courses Available" : "No Courses Created"}
                  </h3>
                  <p className="text-gray-500 mb-4">
                    {isAdmin 
                      ? "There are no courses in the system yet." 
                      : "You haven't created any courses yet. Create a course to start managing enrollments."
                    }
                  </p>
                  {!isAdmin && (
                    <button
                      onClick={() => window.location.href = '/collection/courses'}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <PlusIcon className="h-4 w-4 mr-2" />
                      Create Your First Course
                    </button>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {courses.map((course) => {
                  const enrollmentCount = enrollments.filter(e => e.courseId === course.id).length;
                  const activeCount = enrollments.filter(e => e.courseId === course.id && !e.isExpired && e.status === "active").length;
                  
                  return (
                    <button
                      key={course.id}
                      onClick={() => setSelectedCourse(course.id)}
                      className="group p-3 sm:p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50 transition-all duration-200 text-left hover:shadow-md"
                    >
                      {/* Mobile Layout */}
                      <div className="block sm:hidden">
                        <div className="flex items-center mb-2">
                          {course.image_url ? (
                            <img
                              src={course.image_url}
                              alt={course.name}
                              className="h-8 w-8 rounded-lg object-cover mr-2 shadow-sm border border-gray-200 group-hover:scale-110 transition-transform duration-200"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`h-8 w-8 rounded-lg flex items-center justify-center mr-2 shadow-sm group-hover:scale-110 transition-transform duration-200 ${
                              course.image_url ? 'hidden' : 'bg-gradient-to-br from-purple-500 to-blue-600'
                            }`}
                          >
                            <AcademicCapIcon className="h-4 w-4 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-900 text-sm line-clamp-2 leading-tight">
                              {course.name}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <div className="flex items-center space-x-3">
                            <div className="flex items-center text-green-600">
                              <CheckCircleIcon className="h-3 w-3 mr-1" />
                              <span className="font-medium">{activeCount}</span>
                            </div>
                            <div className="flex items-center text-gray-600">
                              <UsersIcon className="h-3 w-3 mr-1" />
                              <span className="font-medium">{enrollmentCount}</span>
                            </div>
                          </div>
                          <div className="text-blue-600 group-hover:text-blue-700 text-xs">
                            View →
                          </div>
                        </div>
                      </div>

                      {/* Desktop Layout */}
                      <div className="hidden sm:block">
                        <div className="flex items-center mb-3">
                          {course.image_url ? (
                            <img
                              src={course.image_url}
                              alt={course.name}
                              className="h-12 w-12 rounded-lg object-cover mr-3 shadow-md border border-gray-200 group-hover:scale-110 transition-transform duration-200"
                              onError={(e) => {
                                e.target.style.display = 'none';
                                e.target.nextSibling.style.display = 'flex';
                              }}
                            />
                          ) : null}
                          <div 
                            className={`h-12 w-12 rounded-lg flex items-center justify-center mr-3 shadow-md group-hover:scale-110 transition-transform duration-200 ${
                              course.image_url ? 'hidden' : 'bg-gradient-to-br from-purple-500 to-blue-600'
                            }`}
                          >
                            <AcademicCapIcon className="h-6 w-6 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-gray-900 truncate">
                              {course.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              ID: {course.id.slice(0, 8)}...
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <div className="flex items-center space-x-4">
                            <div className="flex items-center text-green-600">
                              <CheckCircleIcon className="h-4 w-4 mr-1" />
                              <span className="font-medium">{activeCount}</span>
                              <span className="text-gray-500 ml-1">active</span>
                            </div>
                            <div className="flex items-center text-gray-600">
                              <UsersIcon className="h-4 w-4 mr-1" />
                              <span className="font-medium">{enrollmentCount}</span>
                              <span className="text-gray-500 ml-1">total</span>
                            </div>
                          </div>
                          <div className="text-blue-600 group-hover:text-blue-700">
                            View →
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Enrollments Table */}
        {selectedCourse && (
          <>
            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Course
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Enrolled Date
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Expiry Date
                      </th>
                      <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredEnrollments.map((enrollment) => (
                      <React.Fragment key={enrollment.id}>
                        <tr className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                <UserIcon className="h-5 w-5 text-blue-600" />
                              </div>
                              <div>
                                <div className="text-sm font-medium text-gray-900">
                                  {enrollment.userData?.name || "Unknown User"}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {enrollment.userData?.email || "No email"}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {enrollment.courseData?.image_url ? (
                                <img
                                  src={enrollment.courseData.image_url}
                                  alt={enrollment.courseData?.name || "Course"}
                                  className="h-14 w-14 rounded-lg object-cover mr-4 shadow-lg border-2 border-gray-200 hover:border-blue-300 transition-all duration-200"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div 
                                className={`h-14 w-14 rounded-lg flex items-center justify-center mr-4 shadow-lg ${
                                  enrollment.courseData?.image_url ? 'hidden' : 'bg-gradient-to-br from-purple-500 to-blue-600'
                                }`}
                              >
                                <AcademicCapIcon className="h-7 w-7 text-white" />
                              </div>
                              <div>
                                <div className="text-sm font-semibold text-gray-900">
                                  {enrollment.courseData?.name || "Unknown Course"}
                                </div>
                                <div className="text-xs text-gray-500 flex items-center mt-1">
                                  <span>ID: {enrollment.courseId.slice(0, 8)}...</span>
                                  {enrollment.courseData?.price && (
                                    <span className="ml-2 px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                      ${enrollment.courseData.price}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                                enrollment
                              )}`}
                            >
                              {getStatusIcon(enrollment)}
                              <span className="ml-1">{getStatusText(enrollment)}</span>
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center">
                              <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                              <span>
                                {enrollment.enrolledAt
                                  ? parseDate(enrollment.enrolledAt).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })
                                  : "N/A"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            <div className="flex items-center">
                              <CalendarIcon className="h-4 w-4 text-gray-400 mr-2" />
                              <span className={enrollment.isExpired ? 'text-red-600 font-medium' : ''}>
                                {enrollment.expireDate
                                  ? parseDate(enrollment.expireDate).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'short',
                                      day: 'numeric'
                                    })
                                  : "No expiry"}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium">
                            <div className="flex items-center justify-center space-x-2">
                              <button
                                onClick={() => toggleRowExpansion(enrollment.id)}
                                className="text-blue-600 hover:text-blue-900 p-1 rounded-lg hover:bg-blue-50"
                                title="View Details"
                              >
                                {expandedRows.has(enrollment.id) ? (
                                  <ChevronUpIcon className="h-5 w-5" />
                                ) : (
                                  <ChevronDownIcon className="h-5 w-5" />
                                )}
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Are you sure you want to unenroll ${enrollment.userData?.name || 'this student'} from this course?`)) {
                                    handleUnenrollStudent(enrollment.id, enrollment.userId);
                                  }
                                }}
                                className="text-red-600 hover:text-red-900 p-1 rounded-lg hover:bg-red-50"
                                title="Unenroll Student"
                              >
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedRows.has(enrollment.id) && (
                          <tr>
                            <td colSpan="6" className="px-6 py-4 bg-gray-50">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                                    Student Details
                                  </h4>
                                  <div className="text-sm text-gray-600 space-y-1">
                                    <div>
                                      <span className="font-medium">Name:</span>{" "}
                                      {enrollment.userData?.name || "N/A"}
                                    </div>
                                    <div>
                                      <span className="font-medium">Email:</span>{" "}
                                      {enrollment.userData?.email || "N/A"}
                                    </div>
                                    <div>
                                      <span className="font-medium">Phone:</span>{" "}
                                      {enrollment.userData?.phone || "N/A"}
                                    </div>
                                    <div>
                                      <span className="font-medium">Role:</span>{" "}
                                      {enrollment.userData?.role || "student"}
                                    </div>
                                  </div>
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium text-gray-900 mb-2">
                                    Course Details
                                  </h4>
                                  <div className="text-sm text-gray-600 space-y-1">
                                    <div>
                                      <span className="font-medium">Course:</span>{" "}
                                      {enrollment.courseData?.name || "N/A"}
                                    </div>
                                    <div>
                                      <span className="font-medium">Description:</span>{" "}
                                      {enrollment.courseData?.description?.slice(0, 100) ||
                                        "N/A"}
                                      {enrollment.courseData?.description?.length > 100
                                        ? "..."
                                        : ""}
                                    </div>
                                    <div>
                                      <span className="font-medium">Price:</span>{" "}
                                      {enrollment.courseData?.price
                                        ? `$${enrollment.courseData.price}`
                                        : "Free"}
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <div className="mt-4 pt-4 border-t border-gray-200">
                                <h4 className="text-sm font-medium text-gray-900 mb-2">
                                  Enrollment Details
                                </h4>
                                <div className="text-sm text-gray-600 space-y-1">
                                  <div>
                                    <span className="font-medium">Enrollment ID:</span>{" "}
                                    {enrollment.id}
                                  </div>
                                  <div>
                                    <span className="font-medium">Enrolled At:</span>{" "}
                                    {enrollment.enrolledAt
                                      ? new Date(enrollment.enrolledAt).toLocaleString()
                                      : "N/A"}
                                  </div>
                                  <div>
                                    <span className="font-medium">Expires At:</span>{" "}
                                    {enrollment.expireDate
                                      ? new Date(enrollment.expireDate).toLocaleString()
                                      : "No expiry"}
                                  </div>
                                  <div>
                                    <span className="font-medium">Status:</span>{" "}
                                    {enrollment.status || "active"}
                                  </div>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>

              {filteredEnrollments.length === 0 && (
                <div className="text-center py-12">
                  <UsersIcon className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No enrollments found
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    {searchQuery || statusFilter !== "active"
                      ? "Try adjusting your filters to see more results."
                      : `No students have been enrolled in this course yet.`}
                  </p>
                </div>
              )}
            </div>
            
            {/* Show total enrollments info when course is selected */}
            <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <AcademicCapIcon className="h-5 w-5 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-900">
                    Showing enrollments for: {courses.find(c => c.id === selectedCourse)?.name || "Selected Course"}
                  </span>
                </div>
                <div className="text-sm text-blue-700">
                  {filteredEnrollments.length} of {enrollments.filter(e => e.courseId === selectedCourse).length} enrollments
                </div>
              </div>
            </div>
          </>
        )}
      </>

      {/* Enroll Students Modal */}
      {showEnrollModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                  Enroll Students in {courses.find(c => c.id === selectedCourse)?.name}
                </h3>
                <button
                  onClick={() => setShowEnrollModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            <div className="p-4 sm:p-6">
              {/* Expiry Date */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Enrollment Expiry Date
                </label>
                <input
                  type="datetime-local"
                  value={expireDate}
                  onChange={(e) => setExpireDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Available Students */}
              <div className="mb-4">
                <h4 className="text-lg font-semibold text-gray-900 mb-3">
                  Available Students ({getAvailableStudents().length})
                </h4>
                <div className="max-h-64 overflow-y-auto border border-gray-200 rounded-lg">
                  {getAvailableStudents().map((user) => (
                    <div key={user.id} className="flex items-center p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                      <input
                        type="checkbox"
                        checked={selectedStudents.includes(user.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedStudents([...selectedStudents, user.id]);
                          } else {
                            setSelectedStudents(selectedStudents.filter(id => id !== user.id));
                          }
                        }}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                      />
                      <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                        <UserIcon className="h-4 w-4 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{user.name || "Unknown User"}</div>
                        <div className="text-sm text-gray-500">{user.email || "No email"}</div>
                      </div>
                    </div>
                  ))}
                  {getAvailableStudents().length === 0 && (
                    <div className="p-6 text-center text-gray-500">
                      All students are already enrolled in this course.
                    </div>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  onClick={() => setShowEnrollModal(false)}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleEnrollStudents}
                  disabled={selectedStudents.length === 0}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                >
                  Enroll {selectedStudents.length} Student{selectedStudents.length !== 1 ? 's' : ''}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnrolledStudentsPage;
